import { ArrowRight, FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CoverageMentionSchema } from '@/services/types/coverage-types';

interface RecentMentionsProps {
  mentions: CoverageMentionSchema[];
  onMentionSelect: (mentionId: string) => void;
}

export const RecentMentions = ({
  mentions,
  onMentionSelect,
}: RecentMentionsProps) => {
  // Sort by id (assuming newer mentions have higher ids) and take first 5
  const recentMentions = mentions
    .slice()
    .sort((a, b) => b.id.localeCompare(a.id))
    .slice(0, 5);

  const getStatusColor = (status: string | null) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      PROCESSING: 'bg-blue-100 text-blue-800',
      READY: 'bg-green-100 text-green-800',
      ACCEPTED: 'bg-emerald-100 text-emerald-800',
      REJECTED: 'bg-red-100 text-red-800',
      ERROR: 'bg-red-100 text-red-800',
    };
    return colors[status || 'UNKNOWN'] || 'bg-gray-100 text-gray-800';
  };

  const getDisplayText = (mention: CoverageMentionSchema) => {
    if (mention.extraction?.carrier?.name) {
      return mention.extraction.carrier.name;
    }
    if (mention.extraction?.policy_type) {
      return mention.extraction.policy_type;
    }
    if (mention.extraction?.category) {
      return mention.extraction.category.replace('_', ' ');
    }
    return 'Coverage mention';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Recent Mentions</CardTitle>
      </CardHeader>
      <CardContent>
        {recentMentions.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>No mentions found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentMentions.map((mention) => (
              <div
                key={mention.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge
                      variant="secondary"
                      className={getStatusColor(mention.status)}
                    >
                      {mention.status || 'Unknown'}
                    </Badge>
                    <span className="text-xs text-gray-500 truncate">
                      {mention.artifact_type} • {mention.artifact_id.slice(-8)}
                    </span>
                  </div>

                  {mention.extraction?.policy_type && (
                    <p className="text-xs text-gray-500 truncate">
                      {mention.extraction.policy_type}
                    </p>
                  )}

                  {getDisplayText(mention)}

                  <div className="flex items-center gap-2 mt-1">
                    {mention.validation?.is_valid_coverage_mention && (
                      <Badge variant="outline" className="text-xs">
                        Valid
                      </Badge>
                    )}
                    {mention.extraction?.confidence && (
                      <Badge variant="outline" className="text-xs">
                        {(mention.extraction.confidence * 100).toFixed(0)}%
                        confidence
                      </Badge>
                    )}
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMentionSelect(mention.id)}
                  className="ml-2 shrink-0"
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
